// "The Pack Formation" — canvas engine.
// Implements WEBTROVA_HERO_BLUEPRINT.md sections 5 (timeline) and 6 (render
// pipeline). Plain JS, no React — createScene(canvas, opts) owns every pixel.

import { LOGO_PATHS, LOGO_CENTER, LOGO_SCALE } from "../data/logoPaths";
import { LOGO_POLYS, LOGO_BONDS } from "../data/logoPoints";

// ---------------------------------------------------------------------------
// Timeline (section 5) — single source of truth for pacing.
// ---------------------------------------------------------------------------
export const T = {
  coreIgnite: 0.3,
  coreIgniteDur: 1.2,
  converge: 1.3,
  convergeDur: 2.1,
  edges: 2.9,
  edgeDur: 0.6,
  lockIn: 4.0,
  lockDur: 1.3,
  dial: 3.8,
  dialDur: 2.0,
  ridgesRise: 0.0,
  ridgesDur: 2.6,
  bondsFade: 6.0,
  bondsFadeDur: 1.5,
};

export const CHAPTER_STARTS = [0, 1.4, 2.9, 4.4];
export const INTRO_END = 7;
export const CYCLE = 5;
export const PULSE_DUR = 1.6;

export const CHAPTERS = [
  {
    key: "instinct",
    label: "Instinct",
    trait: "Independence & Autonomy",
  },
  {
    key: "strategy",
    label: "Strategy",
    trait: "High Intelligence & Strategy",
  },
  {
    key: "loyalty",
    label: "Loyalty",
    trait: "Unwavering Loyalty & Strong Social Bonds",
  },
  {
    key: "resilience",
    label: "Resilience",
    trait: "Direct & Authentic",
  },
];

// ---------------------------------------------------------------------------
// Easing + seeded rng (section 3 easing.ts)
// ---------------------------------------------------------------------------
export const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
export const easeOutCubic = (t) => 1 - Math.pow(1 - clamp(t, 0, 1), 3);
export const easeInOutCubic = (t) => {
  const x = clamp(t, 0, 1);
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
};

function mulberry32(seed) {
  let a = seed >>> 0;
  return function rng() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const rng = mulberry32(1337);

// ---------------------------------------------------------------------------
// Precomputed node data — scatter origin, depth, distance-from-core.
// ---------------------------------------------------------------------------
const coreCentroid = (() => {
  const core = LOGO_POLYS[0].p;
  const sx = core.reduce((s, [x]) => s + x, 0) / core.length;
  const sy = core.reduce((s, [, y]) => s + y, 0) / core.length;
  return [sx, sy];
})();

const NODES = LOGO_POLYS.flatMap((shape, si) =>
  shape.p.map(([tx, ty], vi) => {
    const r1 = rng();
    const r2 = rng();
    const angle = r1 * Math.PI * 2;
    const radius = 1.6 + r2 * 1.4;
    const d = Math.hypot(tx - coreCentroid[0], ty - coreCentroid[1]);
    return {
      si,
      vi,
      core: Boolean(shape.core) && si === 0,
      tx,
      ty,
      sx: Math.cos(angle) * radius,
      sy: Math.sin(angle) * radius,
      z: rng() * 2 - 1,
      delay: rng() * 0.9,
      d,
    };
  })
);

const maxD = Math.max(...NODES.map((n) => n.d));
NODES.forEach((n) => {
  n.dNorm = maxD > 0 ? n.d / maxD : 0;
});

// Flat node index lookup for edges within a shape + bonds across shapes.
const nodeIndex = new Map();
NODES.forEach((n, i) => nodeIndex.set(`${n.si}:${n.vi}`, i));

const EDGES = LOGO_POLYS.flatMap((shape, si) =>
  shape.p.map((_, vi) => {
    const next = (vi + 1) % shape.p.length;
    const a = nodeIndex.get(`${si}:${vi}`);
    const b = nodeIndex.get(`${si}:${next}`);
    return { a, b, d: NODES[a].dNorm, bond: false };
  })
);

const BONDS = LOGO_BONDS.map(([pa, ia, pb, ib]) => {
  const a = nodeIndex.get(`${pa}:${ia}`);
  const b = nodeIndex.get(`${pb}:${ib}`);
  return { a, b, d: Math.max(NODES[a].dNorm, NODES[b].dNorm), bond: true };
});

// ---------------------------------------------------------------------------
// Path2D objects — built once (section 6.5 fidelity fix).
// ---------------------------------------------------------------------------
let FULL_PATHS = null;
let CORE_PATH = null;
function getPaths() {
  if (!FULL_PATHS) {
    FULL_PATHS = LOGO_PATHS.map((d) => new Path2D(d));
    // The core W is the first subpath of LOGO_PATHS[0] — rebuild just that
    // ring as its own path so it can stay bright on top after lock-in.
    const core = LOGO_POLYS[0].p;
    const d = core
      .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`)
      .join(" ") + " Z";
    CORE_PATH = new Path2D(d);
  }
  return { full: FULL_PATHS, core: CORE_PATH };
}

// ---------------------------------------------------------------------------
// Ridges — precomputed per resize (section 6.6).
// ---------------------------------------------------------------------------
function buildRidges(w, h) {
  const layers = [];
  for (let i = 0; i < 3; i++) {
    const base = h * (0.8 + i * 0.065);
    const amp = h * (0.1 - i * 0.022);
    const seed = mulberry32(7 + i * 91);
    const steps = 14;
    const pts = [];
    for (let s = 0; s <= steps; s++) {
      const x = (w / steps) * s;
      const peak = s % 2 === 0;
      const y = base - (peak ? amp * (0.5 + seed() * 0.5) : amp * seed() * 0.3);
      pts.push([x, y]);
    }
    layers.push({ pts, base });
  }
  return layers;
}

// ---------------------------------------------------------------------------
// Dust field — precomputed per resize (section 6.2).
// ---------------------------------------------------------------------------
function buildDust(w, h) {
  // Capped well below the blueprint's 140 — dust links are O(n^2) per
  // frame, and this is a continuously-running background layer, not a
  // one-off effect, so the per-frame cost compounds on weaker devices.
  const count = Math.min(70, Math.floor((w * h) / 20000));
  const seed = mulberry32(42);
  const dust = [];
  for (let i = 0; i < count; i++) {
    dust.push({
      x: seed() * w,
      y: seed() * h,
      vx: (seed() - 0.5) * 6,
      vy: (seed() - 0.5) * 6,
      r: 0.5 + seed() * 1.1,
      phase: seed() * Math.PI * 2,
    });
  }
  return dust;
}

// ---------------------------------------------------------------------------
// Chapter resolver (section 5)
// ---------------------------------------------------------------------------
function resolveChapter(t, cycleBase) {
  if (t < INTRO_END) {
    let idx = 0;
    for (let i = 0; i < CHAPTER_STARTS.length; i++) {
      if (CHAPTER_STARTS[i] <= t) idx = i;
    }
    return idx;
  }
  const since = Math.max(0, t - cycleBase);
  return Math.floor(since / CYCLE) % CHAPTERS.length;
}

// ---------------------------------------------------------------------------
// createScene
// ---------------------------------------------------------------------------
export function createScene(canvas, opts = {}) {
  const { onChapter, reducedMotion = false } = opts;
  const ctx = canvas.getContext("2d");

  let w = 0;
  let h = 0;
  let dpr = 1;
  let mark = { cx: 0, cy: 0, s: 0 };
  let ridges = [];
  let dust = [];

  let raf = null;
  let running = false;
  let startTime = null;
  let t = 0; // seconds, since scene start (or frozen for reduced motion)
  let cycleBase = 0;
  let chapterIdx = -1;
  let pulseAt = -10;

  let pointerX = 0;
  let pointerY = 0;
  let smoothX = 0;
  let smoothY = 0;
  let autoSway = !window.matchMedia("(pointer: fine)").matches;

  function layout() {
    const isMobile = w <= 1024 && h >= w;
    if (isMobile) {
      // cy/s tuned so the dial (radius = s*1.3) clears the fixed 80px nav.
      mark = { cx: w * 0.5, cy: h * 0.27, s: Math.min(w * 0.23, h * 0.1) };
    } else {
      mark = { cx: w * 0.72, cy: h * 0.42, s: Math.min(h * 0.21, w * 0.135) };
    }
    ridges = buildRidges(w, h);
    dust = buildDust(w, h);
  }

  function resize(cssW, cssH) {
    const nextDpr = Math.min(window.devicePixelRatio || 1, 2);
    // Reassigning canvas.width/height always clears the bitmap, even to an
    // identical value — guard against the redundant call ResizeObserver
    // fires right after our own synchronous initial sizing.
    if (w === cssW && h === cssH && dpr === nextDpr) return;

    w = cssW;
    h = cssH;
    dpr = nextDpr;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    layout();

    // A real resize wipes the bitmap — in reduced-motion mode there's no
    // RAF loop to repaint it, so redraw the single frame immediately.
    if (reducedMotion && running) renderFrame(0, 0);
  }

  function setPointer(x, y) {
    pointerX = clamp((x / w) * 2 - 1, -1, 1);
    pointerY = clamp((y / h) * 2 - 1, -1, 1);
  }

  // --- per-layer draw functions -------------------------------------------

  function drawBackground(mx, my) {
    ctx.fillStyle = "#0b0b0b";
    ctx.fillRect(0, 0, w, h);
    const glowP = easeOutCubic(clamp(t / 3, 0, 1)) * 0.1;
    if (glowP > 0.001) {
      const g = ctx.createRadialGradient(
        mark.cx + mx * 10,
        mark.cy + my * 10,
        0,
        mark.cx,
        mark.cy,
        mark.s * 5
      );
      g.addColorStop(0, `rgba(178,241,66,${glowP})`);
      g.addColorStop(1, "rgba(178,241,66,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
    }
  }

  function drawDust(mx, my) {
    const px = mx * 6;
    const py = my * 6;
    ctx.fillStyle = "rgba(230,235,220,0.5)";
    for (const p of dust) {
      const x = (((p.x + px + t * p.vx) % w) + w) % w;
      const y = (((p.y + py + t * p.vy) % h) + h) % h;
      p._x = x;
      p._y = y;
      const tw = 0.5 + 0.5 * Math.sin(t * 1.4 + p.phase);
      ctx.globalAlpha = 0.25 + tw * 0.35;
      ctx.beginPath();
      ctx.arc(x, y, p.r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    ctx.strokeStyle = "rgba(178,241,66,1)";
    ctx.lineWidth = 1;
    for (let i = 0; i < dust.length; i++) {
      for (let j = i + 1; j < dust.length; j++) {
        const a = dust[i];
        const b = dust[j];
        const dx = a._x - b._x;
        const dy = a._y - b._y;
        const d2 = dx * dx + dy * dy;
        if (d2 < 9025) {
          ctx.globalAlpha = 0.07 * (1 - d2 / 9000);
          if (ctx.globalAlpha <= 0) continue;
          ctx.beginPath();
          ctx.moveTo(a._x, a._y);
          ctx.lineTo(b._x, b._y);
          ctx.stroke();
        }
      }
    }
    ctx.globalAlpha = 1;
  }

  function drawMoonDial(mx, my) {
    const p = easeOutCubic(clamp((t - T.dial) / T.dialDur, 0, 1));
    if (p <= 0) return;
    const cx = mark.cx + mx * 4;
    const cy = mark.cy + my * 4;
    const r = mark.s * 1.3;
    const rotation = t * 0.03;

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rotation);
    ctx.strokeStyle = `rgba(178,241,66,${0.3 * p})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(0, 0, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * p);
    ctx.stroke();

    const ticks = 96;
    const visible = Math.floor(ticks * p);
    for (let i = 0; i < visible; i++) {
      const a = (i / ticks) * Math.PI * 2;
      const major = i % 8 === 0;
      const len = major ? 14 : 6;
      const alpha = (major ? 0.5 : 0.16) * p;
      ctx.strokeStyle = `rgba(178,241,66,${alpha})`;
      ctx.lineWidth = major ? 1.4 : 1;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * r, Math.sin(a) * r);
      ctx.lineTo(Math.cos(a) * (r - len), Math.sin(a) * (r - len));
      ctx.stroke();
    }
    ctx.restore();
  }

  function drawMark(mx, my) {
    const breathe = 1 + 0.008 * Math.sin(t * 1.1);
    const igniteP = easeOutCubic(clamp((t - T.coreIgnite) / T.coreIgniteDur, 0, 1));
    const lockP = easeOutCubic(clamp((t - T.lockIn) / T.lockDur, 0, 1));
    const parallaxP = 1 - clamp((t - (T.lockIn + T.lockDur * 0.3)) / (T.lockDur * 0.7), 0, 1);
    const bondsFadeP = clamp((t - T.bondsFade) / T.bondsFadeDur, 0, 1);

    const markParallaxX = mx * 10;
    const markParallaxY = my * 8;

    // --- assembled points/edges (traced story) ---
    const positions = NODES.map((n) => {
      const cp = easeOutCubic(clamp((t - (T.converge + n.delay)) / T.convergeDur, 0, 1));
      const ox = n.sx * mark.s;
      const oy = n.sy * mark.s;
      const fx = n.tx * mark.s * breathe;
      const fy = n.ty * mark.s * breathe;
      let x = ox + (fx - ox) * cp;
      let y = oy + (fy - oy) * cp;
      // per-node depth parallax during assembly, blended to 0 by lock-in
      x += mx * n.z * 14 * parallaxP;
      y += my * n.z * 10 * parallaxP;
      return {
        x: mark.cx + x + markParallaxX * parallaxP,
        y: mark.cy + y + markParallaxY * parallaxP,
        cp,
      };
    });

    const outlineBaseAlpha = 0.62 * (1 - 0.37 * lockP); // settles to .25 by lock
    ctx.lineWidth = 1;
    for (const e of EDGES) {
      const ep = easeOutCubic(clamp((t - (T.edges + e.d * 1.4)) / T.edgeDur, 0, 1));
      if (ep <= 0) continue;
      const a = positions[e.a];
      const b = positions[e.b];
      ctx.strokeStyle = `rgba(178,241,66,${outlineBaseAlpha * ep})`;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(a.x + (b.x - a.x) * ep, a.y + (b.y - a.y) * ep);
      ctx.stroke();
    }
    for (const e of BONDS) {
      const bp = easeOutCubic(clamp((t - (T.edges + e.d * 1.4 + 0.35)) / T.edgeDur, 0, 1));
      if (bp <= 0) continue;
      const a = positions[e.a];
      const b = positions[e.b];
      const alpha = (0.13 - 0.07 * bondsFadeP) * bp; // settles to .06
      ctx.strokeStyle = `rgba(178,241,66,${Math.max(alpha, 0)})`;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(a.x + (b.x - a.x) * bp, a.y + (b.y - a.y) * bp);
      ctx.stroke();
    }

    const dotAlpha = 1 - 0.5 * lockP; // settles to .5
    for (let i = 0; i < NODES.length; i++) {
      const n = NODES[i];
      const pos = positions[i];
      if (pos.cp <= 0 || n.core) continue;
      ctx.fillStyle = `rgba(178,241,66,${0.55 * dotAlpha * pos.cp})`;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, 1.1 + 0.9 * pos.cp, 0, Math.PI * 2);
      ctx.fill();
    }

    // --- core W ignite (always on, flickers until settled) ---
    // Unlike the rest of the mark, the core never scatters — it ignites
    // directly at its true shape (blueprint: "the W ignites alone").
    const coreNodes = NODES.filter((n) => n.core).map((n) => ({
      x: mark.cx + n.tx * mark.s * breathe + markParallaxX * parallaxP,
      y: mark.cy + n.ty * mark.s * breathe + markParallaxY * parallaxP,
    }));
    if (coreNodes.length && igniteP > 0) {
      const flicker = igniteP < 1 ? 0.82 + 0.18 * Math.sin(t * 14) : 1;
      ctx.save();
      ctx.shadowColor = "rgba(178,241,66,0.9)";
      ctx.shadowBlur = 18 + 8 * Math.sin(t * 6);
      ctx.fillStyle = `rgba(245,246,243,${igniteP * flicker})`;
      ctx.beginPath();
      coreNodes.forEach((p, i) => {
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    // --- exact lock-in (fidelity fix, section 6.5) ---
    if (lockP > 0) {
      const { full, core } = getPaths();
      const flash = 0.5 * Math.max(0, 1 - Math.abs(t - (T.lockIn + 0.45)) / 0.18);
      const scale = (mark.s * breathe) / LOGO_SCALE;
      ctx.save();
      ctx.translate(mark.cx + markParallaxX * parallaxP, mark.cy + markParallaxY * parallaxP);
      ctx.scale(scale, scale);
      ctx.translate(-LOGO_CENTER.x, -LOGO_CENTER.y);

      ctx.shadowColor = "rgba(178,241,66,0.55)";
      ctx.shadowBlur = 10 / scale;
      ctx.fillStyle = `rgba(245,246,243,${clamp(0.55 * lockP + flash, 0, 1)})`;
      full.forEach((p) => ctx.fill(p, "evenodd"));

      ctx.lineWidth = 1.2 / scale;
      ctx.lineJoin = "miter";
      ctx.strokeStyle = `rgba(245,246,243,${0.85 * lockP})`;
      full.forEach((p) => ctx.stroke(p));

      ctx.shadowColor = "rgba(178,241,66,0.85)";
      ctx.shadowBlur = (14 + 6 * Math.sin(t * 6)) / scale;
      ctx.fillStyle = `rgba(245,246,243,${lockP})`;
      ctx.fill(core, "evenodd");
      ctx.restore();
    }
  }

  function drawRidges(mx) {
    const riseP = easeOutCubic(clamp((t - T.ridgesRise) / T.ridgesDur, 0, 1));
    const fills = ["#10120d", "#0d0e0b", "#090a08"];
    const strokeAlphas = [0.1, 0.17, 0.26];
    ridges.forEach((layer, i) => {
      const parallax = mx * (6 + i * 12);
      const offsetY = (1 - riseP) * h * 0.25;
      ctx.beginPath();
      ctx.moveTo(0, h);
      layer.pts.forEach(([x, y]) => {
        ctx.lineTo(x + parallax, y + offsetY);
      });
      ctx.lineTo(w, h);
      ctx.closePath();
      ctx.fillStyle = fills[i];
      ctx.globalAlpha = riseP;
      ctx.fill();

      ctx.strokeStyle = `rgba(178,241,66,${strokeAlphas[i] * riseP})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      layer.pts.forEach(([x, y], idx) => {
        const py = y + offsetY;
        if (idx === 0) ctx.moveTo(x + parallax, py);
        else ctx.lineTo(x + parallax, py);
      });
      ctx.stroke();

      // facet lines: each peak down to the valley baseline
      layer.pts.forEach(([x, y], idx) => {
        if (idx % 2 !== 0) return;
        ctx.beginPath();
        ctx.moveTo(x + parallax, y + offsetY);
        ctx.lineTo(x + parallax, layer.base + offsetY);
        ctx.stroke();
      });
      ctx.globalAlpha = 1;
    });
  }

  function drawFx() {
    if (pulseAt >= 0) {
      const pp = clamp((t - pulseAt) / PULSE_DUR, 0, 1);
      if (pp < 1) {
        const r = mark.s * (0.3 + pp * 2.3);
        ctx.strokeStyle = `rgba(178,241,66,${0.28 * (1 - pp)})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(mark.cx, mark.cy, r, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
    const fog = ctx.createLinearGradient(0, h * 0.82, 0, h);
    fog.addColorStop(0, "rgba(0,0,0,0)");
    fog.addColorStop(1, "rgba(0,0,0,0.6)");
    ctx.fillStyle = fog;
    ctx.fillRect(0, h * 0.82, w, h * 0.18);
  }

  function renderFrame(mx, my) {
    ctx.clearRect(0, 0, w, h);
    drawBackground(mx, my);
    drawDust(mx, my);
    drawMoonDial(mx, my);
    drawMark(mx, my);
    drawRidges(mx);
    drawFx();
  }

  function frame(now) {
    if (!running) return;
    if (startTime === null) startTime = now;
    t = (now - startTime) / 1000;

    if (autoSway) {
      smoothX = Math.sin(t * 0.25) * 0.35;
      smoothY = Math.cos(t * 0.2) * 0.2;
    } else {
      smoothX += (pointerX - smoothX) * 0.05;
      smoothY += (pointerY - smoothY) * 0.05;
    }

    renderFrame(smoothX, smoothY);

    const idx = resolveChapter(t, cycleBase);
    if (idx !== chapterIdx) {
      if (chapterIdx !== -1 && t >= T.lockIn + T.lockDur) pulseAt = t;
      chapterIdx = idx;
      onChapter?.(idx, CYCLE);
    }

    raf = requestAnimationFrame(frame);
  }

  function start() {
    if (running) return;
    running = true;
    if (reducedMotion) {
      t = 30;
      cycleBase = 0;
      chapterIdx = CHAPTERS.length - 1;
      onChapter?.(chapterIdx, CYCLE);
      renderFrame(0, 0);
      return;
    }
    raf = requestAnimationFrame(frame);
  }

  function stop() {
    running = false;
    if (raf) cancelAnimationFrame(raf);
    raf = null;
  }

  function jumpTo(idx) {
    chapterIdx = idx;
    cycleBase = t - idx * CYCLE;
    pulseAt = t;
    onChapter?.(idx, CYCLE);
  }

  return { start, stop, resize, setPointer, jumpTo };
}
